import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import ViewWrapper from '../../elements/ViewWrapper';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchPermissions,
  getAllPermissions,
  getFetchingPermissions,
} from '../../../store/features/acl/permissions/permissionsSlice';
import { fetchRolesWithParams } from '../../../store/features/acl/roles/rolesSlice';
import { PermissionModel } from '../../../models/permission';
import { RoleModel } from '../../../models/role';

import './PermissionsListView.less';

const { Text } = Typography;

const ROLES_FETCH_LIMIT = 500;

const PermissionsListView = () => {
  const dispatch = useAppDispatch();
  const allPermissions = useAppSelector(getAllPermissions);
  const fetchingPermissions = useAppSelector(getFetchingPermissions);
  const rolesFromStore = useAppSelector((state) => state.roles.value);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const sortedPermissions = useMemo(() => {
    if (!allPermissions.length) return [];
    return [...allPermissions].sort((a, b) =>
      (a.display_name || a.name).localeCompare(b.display_name || b.name)
    );
  }, [allPermissions]);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchRolesWithParams({
        params: {
          _limit: ROLES_FETCH_LIMIT,
          _offset: 0,
          _columns: 'permissions.*',
        },
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (sortedPermissions.length > 0) {
      setExpandedRowKeys(sortedPermissions.map((r) => r.id));
    }
  }, [sortedPermissions]);

  const permissionToRolesMap = useMemo(() => {
    const map = new Map<string, RoleModel[]>();
    for (const role of rolesFromStore ?? []) {
      const perms = role.permissions ?? [];
      for (const perm of perms) {
        const name = typeof perm === 'string' ? perm : perm?.name ?? '';
        if (!name) continue;
        const list = map.get(name) ?? [];
        if (!list.some((r) => String(r.id) === String(role.id))) list.push(role);
        map.set(name, list);
      }
    }
    return map;
  }, [rolesFromStore]);

  const allExpanded =
    sortedPermissions.length > 0 &&
    expandedRowKeys.length === sortedPermissions.length;

  const handleToggleExpandAll = useCallback(() => {
    setExpandedRowKeys(
      allExpanded ? [] : sortedPermissions.map((r) => r.id)
    );
  }, [allExpanded, sortedPermissions]);

  const columns: ColumnsType<PermissionModel> = useMemo(
    () => [
      {
        title: 'Display name',
        dataIndex: 'display_name',
        key: 'display_name',
        width: '40%',
        render: (displayName: string, record: PermissionModel) => (
          <Text>{displayName || record.name}</Text>
        ),
      },
      {
        title: 'Short name',
        dataIndex: 'name',
        key: 'name',
        width: '30%',
        render: (name: string) => (
          <Text className="permission-key">{name}</Text>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (description: string) => (
          <Text type="secondary">{description || '—'}</Text>
        ),
      },
    ],
    []
  );

  const expandable = useMemo(
    () => ({
      expandedRowKeys,
      onExpand: (expanded: boolean, record: PermissionModel) => {
        setExpandedRowKeys((prev) =>
          expanded
            ? [...prev, record.id]
            : prev.filter((key) => key !== record.id)
        );
      },
      expandedRowRender: (record: PermissionModel) => {
        const roles = permissionToRolesMap.get(record.name) ?? [];
        const label =
          roles.length > 0
            ? roles.map((r) => r.display_name || r.name).join(', ')
            : '—';
        return (
          <div className="permissions-list-inherited-row">
            <Text type="secondary">
              <strong>Included in roles:</strong> {label}
            </Text>
          </div>
        );
      },
    }),
    [permissionToRolesMap, expandedRowKeys]
  );

  return (
    <ViewWrapper headerTitle="Permissions">
      <Space direction="vertical" size="middle" className="permissions-list-content">
        <Space style={{ marginBottom: 8 }}>
          <Button
            icon={allExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={handleToggleExpandAll}
            disabled={sortedPermissions.length === 0}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </Space>
        <Table
          loading={fetchingPermissions && sortedPermissions.length === 0}
          rowKey="id"
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} permissions`,
          }}
          columns={columns}
          dataSource={sortedPermissions}
          expandable={expandable}
        />
      </Space>
    </ViewWrapper>
  );
};

export default PermissionsListView;
